// SRVCreateListing.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../axiosSetup";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const SRVCreateListing = ({ onCreated }) => {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [expiresTouched, setExpiresTouched] = useState(false);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [feedback, setFeedback] = useState(null);
  const feedbackRef = useRef(null);

  const token = localStorage.getItem("authToken");

  const showFeedback = (type, text, timeoutMs = 4500) => {
    setFeedback({ type, text });
    setTimeout(() => feedbackRef.current?.focus?.(), 10);
    if (timeoutMs) setTimeout(() => setFeedback(null), timeoutMs);
  };

  const nowRoundedForInput = useMemo(() => {
    const d = dayjs();
    const rounded = d.minute(Math.ceil(d.minute() / 5) * 5).second(0).millisecond(0);
    return rounded.format("YYYY-MM-DDTHH:mm");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/personell-workassignment-swap", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAssignments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        const msg = err?.response?.data?.message || "Fehler beim Laden deiner Einsätze.";
        toast.error(msg);
        showFeedback("error", msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const isExpiresValid = !!expiresAt && dayjs(expiresAt).isValid();
  const expiresError = !isExpiresValid && expiresTouched
    ? "Bitte wähle ein Ablaufdatum (Pflichtfeld)."
    : null;

  const canSubmit = !!selectedAssignmentId && isExpiresValid && !creating;

  const handleCreate = async () => {
    if (!selectedAssignmentId) {
      showFeedback("info", "Bitte wähle zuerst einen Einsatz aus.");
      return toast.info("Bitte wähle zuerst einen Einsatz aus.");
    }

    if (!isExpiresValid) {
      setExpiresTouched(true);
      showFeedback("error", "Ablaufdatum ist verpflichtend. Bitte auswählen.");
      return toast.error("Ablaufdatum ist verpflichtend. Bitte auswählen.");
    }

    const expires = dayjs(expiresAt).format("YYYY-MM-DD HH:mm:ss");
    setCreating(true);

    try {
      await api.post(
        "/api/swap-marketplace/listings",
        {
          assignmentId: Number(selectedAssignmentId),
          reason: reason?.trim() || null,
          expiresAt: expires,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Angebot erfolgreich veröffentlicht.");
      showFeedback("success", "Dein Angebot wurde erfolgreich veröffentlicht.");

      setSelectedAssignmentId(null);
      setReason("");
      setExpiresAt("");
      setExpiresTouched(false);
      onCreated?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Angebot konnte nicht veröffentlicht werden.";
      toast.error(msg);
      showFeedback("error", msg, 7000);
    } finally {
      setCreating(false);
    }
  };

  /* ====================================================================== */
  /*  UI COMPONENTS                                                         */
  /* ====================================================================== */

  const feedbackBg =
    feedback?.type === "success" ? "#065f46" :
    feedback?.type === "error"   ? "#7f1d1d" :
                                   "#1e3a8a";

  const feedbackBorder =
    feedback?.type === "success" ? "#34d399" :
    feedback?.type === "error"   ? "#fca5a5" :
                                   "#93c5fd";

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 24 }}>

      {/* -------- DARK HEADER (wie du wolltest) -------- */}
      <div
        style={{
          padding: "18px 20px",
          borderRadius: 12,
          background: "#1f2937",
          color: "white",
          marginBottom: 22,
          border: "1px solid #374151",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#374151",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M3 7h18M6 12h12M9 17h6" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>

        <h2 style={{ margin: 0, fontSize: 20 }}>Einsatz in der Tauschbörse anbieten</h2>
      </div>

      {/* -------- SECTION: 1) Einsatz -------- */}
      <Section title="1) Wähle deinen Einsatz">
        {loading ? (
          <div>Lade…</div>
        ) : assignments.length === 0 ? (
          <Alert tone="warning">
            Du hast aktuell keine tauschbaren Einsätze.
          </Alert>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 12,
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {assignments.map((a) => {
              const selected = selectedAssignmentId === a.assignmentID;
              return (
                <button
                  key={a.assignmentID}
                  type="button"
                  onClick={() => setSelectedAssignmentId(a.assignmentID)}
                  style={{
                    textAlign: "left",
                    padding: "14px 16px",
                    borderRadius: 12,
                    border: selected ? "2px solid #2563eb" : "1px solid #e5e7eb",
                    background: selected ? "#eff6ff" : "white",
                    transition: ".15s",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{a.assignmentName}</div>
                  {a.dates && (
                    <div style={{ marginTop: 6, color: "#4b5563" }}>
                      {a.dates}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Section>

      {/* -------- SECTION: 2) Grund -------- */}
      <Section title="2) Grund (optional)">
        <textarea
          rows={4}
          placeholder="Warum möchtest du tauschen?"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{
            width: "100%",
            padding: 12,
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            color: "#111",
            background: "white",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Section>

        <div style={{ marginTop: 4, fontSize: ".85rem", color: reason.length >= 450 ? "#ef4444" : "#9ca3af", textAlign: "right" }}>
          {reason.length}/500 Zeichen
        </div>

      {/* -------- SECTION: 3) Ablaufdatum -------- */}
      <Section title="3) Ablaufdatum *">
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          onBlur={() => setExpiresTouched(true)}
          min={nowRoundedForInput}
          style={{
            width: "100%",
            padding: 12,
            borderRadius: 12,
            border: `1px solid ${expiresError ? "#ef4444" : "#e5e7eb"}`,
            background: expiresError ? "#fef2f2" : "#ffffff",
            color: "#111",
            outline: "none",
            WebkitAppearance: "none",
            appearance: "none",
            colorScheme: "light",
          }}
        />
        {expiresError && (
          <div style={{ marginTop: 6, color: "#ef4444" }}>{expiresError}</div>
        )}
        <div style={{ marginTop: 6, fontSize: ".9rem", color: "#6b7280" }}>
          Nach dem gewählten Zeitpunkt gilt das Angebot automatisch als abgelaufen.
        </div>
      </Section>

      {/* -------- SUBMIT BUTTON -------- */}
      <button
        type="button"
        onClick={handleCreate}
        disabled={!canSubmit}
        style={{
          marginTop: 28,
          width: "100%",
          padding: "12px 16px",
          borderRadius: 12,
          border: "none",
          fontWeight: 700,
          color: "white",
          background: canSubmit
            ? "linear-gradient(180deg, #3b82f6, #2563eb)"
            : "#9ca3af",
          cursor: canSubmit ? "pointer" : "not-allowed",
          boxShadow: canSubmit ? "0 4px 12px rgba(37,99,235,.25)" : "none",
        }}
      >
        {creating ? "Veröffentliche…" : "Angebot veröffentlichen"}
      </button>

      {/* -------- Feedback Banner -------- */}
      {feedback && (
        <div
          ref={feedbackRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          style={{
            marginTop: 18,
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${feedbackBorder}`,
            background: feedbackBg,
            color: "white",
            textAlign: "center",
          }}
        >
          {feedback.text}
        </div>
      )}

      {/* FIX: DateTime Inputs hell erzwingen */}
      <DateTimeLightFix />
    </div>
  );
};

/* ====================================================================== */
/*  UI HELPERS                                                            */
/* ====================================================================== */

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{title}</h3>
      {children}
    </section>
  );
}

function Alert({ tone, children }) {
  const styles = {
    warning: { bg: "#fff7ed", border: "#f59e0b55", text: "#92400e" },
  }[tone] || { bg: "#eef2ff", border: "#a5b4fc55", text: "#3730a3" };

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 12,
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        color: styles.text,
      }}
    >
      {children}
    </div>
  );
}

/** globaler Style-Fix gegen Dark‑Mode für datetime-local */
function DateTimeLightFix() {
  return (
    <style>{`
      input[type="datetime-local"] {
        background: #fff !important;
        color: #111 !important;
      }
      input[type="datetime-local"]::-webkit-calendar-picker-indicator {
        filter: invert(0);
      }
      input[type="datetime-local"]::-webkit-datetime-edit-fields-wrapper {
        color: #111 !important;
      }
    `}</style>
  );
}

function isNonEmpty(v) {
  return typeof v === "string" && v.trim().length > 0;
}

export default SRVCreateListing;